import { createContext, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { customerApi } from "../api/client";
import type { CustomerAccount } from "../types/api";

const TOKEN_KEY = "pulsepass_customer_token";
const PROFILE_KEY = "pulsepass_customer_profile";
const ACTIVITY_KEY = "pulsepass_customer_last_activity";
const SESSION_TIMEOUT_MS = (() => {
  const rawValue = Number(import.meta.env.VITE_SESSION_INACTIVITY_MINUTES ?? 30);
  const minutes = Number.isFinite(rawValue) && rawValue > 0 ? rawValue : 30;
  return minutes * 60 * 1000;
})();
const ACTIVITY_WRITE_THROTTLE_MS = 15_000;

type CustomerAuthContextValue = {
  customer: CustomerAccount | null;
  token: string | null;
  isBootstrapping: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithSocial: (payload: { provider: "google" | "apple"; idToken: string; name?: string }) => Promise<void>;
  signUp: (payload: { name: string; email: string; phone: string; password: string }) => Promise<void>;
  signOut: () => void;
};

const CustomerAuthContext = createContext<CustomerAuthContextValue | undefined>(undefined);

function readLastActivity() {
  const storedValue = window.localStorage.getItem(ACTIVITY_KEY);
  const timestamp = storedValue ? Number(storedValue) : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function isInactive(timestamp: number) {
  return timestamp > 0 && Date.now() - timestamp > SESSION_TIMEOUT_MS;
}

export function CustomerAuthProvider({ children }: PropsWithChildren) {
  const [customer, setCustomer] = useState<CustomerAccount | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const lastPersistedActivityRef = useRef(0);

  const clearSession = () => {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(PROFILE_KEY);
    window.localStorage.removeItem(ACTIVITY_KEY);
    setToken(null);
    setCustomer(null);
  };

  const persistActivity = (timestamp = Date.now()) => {
    lastPersistedActivityRef.current = timestamp;
    window.localStorage.setItem(ACTIVITY_KEY, String(timestamp));
  };

  useEffect(() => {
    const storedToken = window.localStorage.getItem(TOKEN_KEY);
    const storedProfile = window.localStorage.getItem(PROFILE_KEY);
    const lastActivity = readLastActivity();

    if (!storedToken || isInactive(lastActivity)) {
      clearSession();
      setIsBootstrapping(false);
      return;
    }

    setToken(storedToken);
    persistActivity(lastActivity || Date.now());

    if (storedProfile) {
      try {
        setCustomer(JSON.parse(storedProfile) as CustomerAccount);
        setIsBootstrapping(false);
      } catch {
        window.localStorage.removeItem(PROFILE_KEY);
      }
    }

    void customerApi
      .me(storedToken)
      .then(({ user }) => {
        setCustomer(user);
        window.localStorage.setItem(PROFILE_KEY, JSON.stringify(user));
        persistActivity();
      })
      .catch(() => {
        clearSession();
      })
      .finally(() => setIsBootstrapping(false));
  }, []);

  useEffect(() => {
    if (!token) {
      return;
    }

    const markActive = () => {
      const now = Date.now();
      if (now - lastPersistedActivityRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
        persistActivity(now);
      }
    };

    const checkInactivity = () => {
      if (isInactive(readLastActivity())) {
        clearSession();
      }
    };

    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((eventName) => window.addEventListener(eventName, markActive, { passive: true }));

    const timer = window.setInterval(checkInactivity, 30_000);

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, markActive));
      window.clearInterval(timer);
    };
  }, [token]);

  const signIn = async (email: string, password: string) => {
    const response = await customerApi.login({ email, password });
    setToken(response.token);
    setCustomer(response.user);
    window.localStorage.setItem(TOKEN_KEY, response.token);
    window.localStorage.setItem(PROFILE_KEY, JSON.stringify(response.user));
    persistActivity();
  };

  const signInWithSocial = async (payload: { provider: "google" | "apple"; idToken: string; name?: string }) => {
    const response = await customerApi.socialLogin(payload);
    setToken(response.token);
    setCustomer(response.user);
    window.localStorage.setItem(TOKEN_KEY, response.token);
    window.localStorage.setItem(PROFILE_KEY, JSON.stringify(response.user));
    persistActivity();
  };

  const signUp = async (payload: { name: string; email: string; phone: string; password: string }) => {
    const response = await customerApi.register(payload);
    setToken(response.token);
    setCustomer(response.user);
    window.localStorage.setItem(TOKEN_KEY, response.token);
    window.localStorage.setItem(PROFILE_KEY, JSON.stringify(response.user));
    persistActivity();
  };

  const signOut = () => {
    clearSession();
  };

  const value = useMemo(
    () => ({
      customer,
      token,
      isBootstrapping,
      signIn,
      signInWithSocial,
      signUp,
      signOut,
    }),
    [customer, token, isBootstrapping]
  );

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error("useCustomerAuth must be used within CustomerAuthProvider");
  }

  return context;
}
